import React from "react";
import { Box, Flex, Grid, GridItem, HStack, SimpleGrid, VStack, keyframes } from "@chakra-ui/react";
import { GLASS } from "./glassUi";

// ── Kompleksowy moduł skeletonów (spójny z „nowym designem": glass + shimmer) ──
// Używany w pulpicie, hubie Społeczności i wszędzie, gdzie ładujemy dane.

const shimmer = keyframes`0% { background-position: -200% 0; } 100% { background-position: 200% 0; }`;

const SHEEN = {
  background: "linear-gradient(90deg, rgba(67,0,152,0.06) 25%, rgba(67,0,152,0.13) 37%, rgba(67,0,152,0.06) 63%)",
  backgroundSize: "200% 100%",
  animation: `${shimmer} 1.4s ease-in-out infinite`
} as const;

// ── prymitywy ──
export const SkelLine: React.FC<{ w?: string | number; h?: string; r?: string; mt?: string; mb?: string }> = ({ w = "100%", h = "12px", r = "7px", mt, mb }) => (
  <Box w={w} h={h} mt={mt} mb={mb} borderRadius={r} sx={SHEEN} flexShrink={0} />
);

export const SkelCircle: React.FC<{ size: string }> = ({ size }) => (
  <Box w={size} h={size} borderRadius="full" sx={SHEEN} flexShrink={0} />
);

export const SkelBox: React.FC<{ w?: string; h?: string; r?: string }> = ({ w = "100%", h = "100%", r = "16px" }) => (
  <Box w={w} h={h} borderRadius={r} sx={SHEEN} />
);

// Kafel glass ze skeletonem w środku
const SkelTile: React.FC<{ children: React.ReactNode; colSpan?: any; rowSpan?: any; p?: string }> = ({ children, colSpan = 1, rowSpan = 1, p = "18px" }) => (
  <GridItem colSpan={colSpan} rowSpan={rowSpan} borderRadius="22px" p={p} sx={GLASS} overflow="hidden">
    {children}
  </GridItem>
);

// ── post (WallPostCard) ──
export const PostSkeleton: React.FC = () => (
  <Box borderRadius="20px" p={{ base: "16px", md: "18px 22px" }} sx={GLASS}>
    <HStack spacing="11px" align="flex-start">
      <SkelCircle size="40px" />
      <Box flex="1">
        <SkelLine w="42%" h="13px" />
        <SkelLine w="26%" h="10px" mt="7px" />
      </Box>
    </HStack>
    <VStack align="stretch" spacing="8px" mt="14px">
      <SkelLine w="100%" />
      <SkelLine w="92%" />
      <SkelLine w="64%" />
    </VStack>
    <HStack spacing="18px" mt="16px" pt="12px" borderTop="1px solid rgba(67,0,152,0.06)">
      <SkelLine w="52px" h="14px" />
      <SkelLine w="52px" h="14px" />
    </HStack>
  </Box>
);

export const FeedSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <VStack align="stretch" spacing="14px" w="100%">
    {Array.from({ length: count }).map((_, i) => (
      <PostSkeleton key={i} />
    ))}
  </VStack>
);

// ── karta (profil / grupa / głosowanie) ──
export const CardSkeleton: React.FC = () => (
  <Box borderRadius="20px" p="16px" sx={GLASS}>
    <HStack justify="space-between" align="flex-start">
      <SkelCircle size="56px" />
      <SkelLine w="88px" h="30px" r="11px" />
    </HStack>
    <SkelLine w="55%" h="14px" mt="14px" />
    <SkelLine w="72%" h="11px" mt="8px" />
    <SkelLine w="90%" h="11px" mt="10px" />
  </Box>
);

export const CardsGridSkeleton: React.FC<{ count?: number; columns?: any }> = ({ count = 6, columns = { base: 1, md: 2 } }) => (
  <SimpleGrid columns={columns} spacing="14px">
    {Array.from({ length: count }).map((_, i) => (
      <CardSkeleton key={i} />
    ))}
  </SimpleGrid>
);

// ── karta głosowania (autorskie / lista) ──
export const VotingCardSkeleton: React.FC = () => (
  <Box borderRadius="18px" p="20px" sx={GLASS}>
    <HStack justify="space-between">
      <SkelLine w="90px" h="22px" r="full" />
      <SkelLine w="70px" h="14px" />
    </HStack>
    <SkelLine w="80%" h="18px" mt="16px" />
    <SkelLine w="50%" h="12px" mt="10px" />
    <SimpleGrid columns={3} spacing="8px" mt="16px">
      {Array.from({ length: 3 }).map((_, i) => (
        <SkelBox key={i} h="52px" r="12px" />
      ))}
    </SimpleGrid>
    <SkelLine w="100%" h="34px" r="10px" mt="16px" />
  </Box>
);

export const VotingsGridSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="16px">
    {Array.from({ length: count }).map((_, i) => (
      <VotingCardSkeleton key={i} />
    ))}
  </SimpleGrid>
);

// ── rail (prawy panel) ──
const RailSection: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <Box>
    <SkelLine w="45%" h="11px" mb="12px" />
    <VStack align="stretch" spacing="10px">
      {Array.from({ length: rows }).map((_, i) => (
        <HStack key={i} spacing="10px">
          <SkelBox w="34px" h="34px" r="10px" />
          <Box flex="1">
            <SkelLine w="70%" h="12px" />
            <SkelLine w="40%" h="9px" mt="6px" />
          </Box>
        </HStack>
      ))}
    </VStack>
  </Box>
);

export const RailSkeleton: React.FC = () => (
  <VStack align="stretch" spacing="20px">
    <HStack spacing="11px" p="9px" borderRadius="15px" bg="rgba(255,255,255,0.5)" border="1px solid rgba(24,12,48,0.06)">
      <SkelCircle size="42px" />
      <Box flex="1">
        <SkelLine w="60%" h="13px" />
        <SkelLine w="38%" h="10px" mt="6px" />
      </Box>
    </HStack>
    <RailSection rows={3} />
    <RailSection rows={2} />
    <RailSection rows={3} />
  </VStack>
);

// ── pulpit (bento) ──
export const KpiSkeleton: React.FC = () => (
  <SkelTile>
    <Flex direction="column" justify="space-between" h="100%">
      <SkelBox w="40px" h="40px" r="13px" />
      <Box>
        <SkelLine w="55%" h="26px" />
        <SkelLine w="70%" h="11px" mt="8px" />
      </Box>
    </Flex>
  </SkelTile>
);

export const DashboardSkeleton: React.FC = () => (
  <Grid
    autoRows={{ base: "144px", lg: "150px" }}
    gap="14px"
    sx={{
      gridTemplateColumns: "repeat(2, 1fr)",
      "@media (min-width: 62em)": { gridTemplateColumns: "repeat(4, 1fr)" },
      "@media (min-width: 120em)": { gridTemplateColumns: "repeat(6, 1fr)" }
    }}
  >
    {/* status 2×2 */}
    <GridItem colSpan={2} rowSpan={2} borderRadius="22px" p="24px" sx={GLASS} overflow="hidden">
      <Flex direction="column" h="100%" justify="space-between">
        <HStack justify="space-between">
          <SkelLine w="120px" h="11px" />
          <SkelCircle size="48px" />
        </HStack>
        <Box>
          <SkelLine w="70%" h="26px" />
          <SkelLine w="90%" h="13px" mt="10px" />
        </Box>
        <HStack spacing="10px">
          <SkelLine w="150px" h="34px" r="12px" />
          <SkelLine w="120px" h="34px" r="12px" />
        </HStack>
      </Flex>
    </GridItem>
    {Array.from({ length: 6 }).map((_, i) => (
      <KpiSkeleton key={i} />
    ))}
    <SkelTile colSpan={2} rowSpan={2}>
      <SkelLine w="45%" h="13px" mb="14px" />
      <VStack align="stretch" spacing="12px">
        {Array.from({ length: 4 }).map((_, i) => (
          <HStack key={i} spacing="11px">
            <SkelBox w="36px" h="36px" r="11px" />
            <Box flex="1">
              <SkelLine w="40%" h="10px" />
              <SkelLine w="75%" h="12px" mt="6px" />
            </Box>
          </HStack>
        ))}
      </VStack>
    </SkelTile>
    <SkelTile colSpan={2} rowSpan={2}>
      <SkelLine w="45%" h="13px" mb="14px" />
      <Flex align="center" gap="16px" h="70%">
        <SkelCircle size="120px" />
        <VStack align="stretch" flex="1" spacing="10px">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkelLine key={i} w="100%" h="12px" />
          ))}
        </VStack>
      </Flex>
    </SkelTile>
  </Grid>
);
